/**
 * Who signed in last on this device, and their tenant (kz2 M12, R11 B). The session restore reads
 * `tenants/{tenantId}` from this hint IN PARALLEL with `users/{uid}`, instead of after it; the result
 * is used only when the profile that comes back has the same uid and tenant (else the restore reads
 * the tenant as before). An identifier pair, never the profile or the tenant document themselves.
 */
export const SESSION_HINT_KEY = 'kz.session.hint.v1';

export interface SessionHint {
  uid: string;
  tenantId: string;
}

type HintSubject = { id?: string | null; tenantId?: string | null } | null | undefined;

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readSessionHint(storage: Storage | null = store()): SessionHint | null {
  try {
    const raw = storage?.getItem(SESSION_HINT_KEY);
    const hint = raw ? (JSON.parse(raw) as Partial<SessionHint>) : null;
    return hint && typeof hint.uid === 'string' && hint.uid && typeof hint.tenantId === 'string' && hint.tenantId
      ? { uid: hint.uid, tenantId: hint.tenantId }
      : null;
  } catch {
    return null;
  }
}

/** Remember `user`'s uid and tenant; a user without both (or `null`, a sign-out) forgets the hint. Never throws. */
export function writeSessionHint(user: HintSubject, storage: Storage | null = store()): void {
  try {
    if (user?.id && user.tenantId) {
      storage?.setItem(SESSION_HINT_KEY, JSON.stringify({ uid: user.id, tenantId: user.tenantId }));
    } else {
      storage?.removeItem(SESSION_HINT_KEY);
    }
  } catch {
    /* blocked storage: the restore reads the tenant after the profile, as before */
  }
}

/** The hint may be used for `user`: same uid, same tenant. */
export function hintMatches(hint: SessionHint | null, user: HintSubject): boolean {
  return !!hint && !!user?.id && hint.uid === user.id && hint.tenantId === user.tenantId;
}
